<template>
  <component
    :is="fieldComponent"
    v-if="shouldRender"
    class="w-full body-font field-component"
    :question="fieldWithChanges"
    :value="fieldValue"
    :document-value="documentValue"
    :config="fieldWithChanges.config"
    :questions="nestedFields"
    :path="path"
  />
</template>

<script>
import _get from 'lodash/get'
import { computed, toRefs, ref, inject } from '@nuxtjs/composition-api'
import matchFieldType from '@/utils/matchFieldTypes'
import { isValueEmpty } from '@/utils/generalUtils'
import useFieldActions from '@/composables/useFieldActions'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { HIDE_EMPTY_FIELDS } from '@/constants/featureFlags'

const ASYNC_DATA_QUESTION = ['courseSchedule']

export default {
  name: 'DynamicLayoutField',
  props: {
    questionId: {
      type: String,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
    documentValue: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line
    value: {
      required: true,
    },
    path: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { documentValue } = toRefs(props)
    const rootLevelFields = inject('questions', [])

    const { hideEmptyFields } = useFeatureFlags({
      hideEmptyFields: HIDE_EMPTY_FIELDS,
    })
    const field = computed(
      () => findQuestionInArray(props.questions, props.questionId) || {}
    )
    const fullPath = [...props.path, field.value.dataKey]
    const rootLevelField = computed(
      () => findQuestionInArray(rootLevelFields, fullPath[0]) || field.value
    )

    const localValue = ref()
    const fieldValue = computed(() => {
      if (localValue.value !== undefined) return localValue.value
      if (rootLevelField.value.custom) {
        return _get(documentValue.value.customFields || {}, fullPath)
      }

      if (props.value) {
        const valueFromNested =
          props.value[
            field.value.dataKey || field.value.questionId || field.value.key
          ]
        if (valueFromNested !== undefined) return valueFromNested
      }
      const valueFromDocument =
        props.documentValue[
          field.value.dataKey || field.value.questionId || field.value.key
        ]
      if (valueFromDocument !== undefined) return valueFromDocument
      return (
        field.value.config &&
        field.value.config.default &&
        field.value.config.default.value
      )
    })
    const onActionUpdate = (value) => {
      localValue.value = value
    }

    const { fieldWithChanges } = useFieldActions({
      field,
      formValue: documentValue,
      fieldValue,
      update: onActionUpdate,
      questions: props.questions,
    })

    const isVisible = computed(() => !fieldWithChanges.value.hidden)
    const nestedFields = computed(
      () =>
        fieldWithChanges.value.fields ||
        (fieldWithChanges.value.config && fieldWithChanges.value.config.fields)
    )

    const fieldComponent = matchFieldType(
      fieldWithChanges.value.questionType,
      nestedFields.value
    )

    const isAsync = computed(() => {
      return ASYNC_DATA_QUESTION.includes(fieldWithChanges.value.questionType)
    })

    const hasValue = computed(() => {
      return !isValueEmpty(fieldValue.value)
    })

    const shouldRender = computed(() => {
      return hideEmptyFields
        ? // for questions having their data loaded asynchronously there's no value to be checked
          // so those have to determine whether to render or not, on their own
          isVisible.value && (isAsync.value || hasValue.value)
        : isVisible.value
    })

    return {
      fieldValue,
      fieldWithChanges,
      isVisible,
      nestedFields,
      fieldComponent,
      shouldRender,
    }
  },
}

function findQuestionInArray(collection, questionId) {
  return collection.find(
    (q) =>
      q.questionId === questionId ||
      q.dataKey === questionId ||
      q.key === questionId
  )
}
</script>

<style lang="css">
/* heading-3 style same as rich content style */
.field-component .field-label {
  margin-bottom: 0;
  font-size: 1.1rem;
  font-weight: bold;
}
.field-component .field-value {
  font-size: 16px;
  line-height: 160%;
  color: var(--textFontColor, inherit);
  font-weight: var(--textFontWeight, inherit);
}
</style>
